import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  SafText,
  SafTable,
  SafPagination,
  SafIcon,
  SafSearchField,
  SafCheckbox,
  SafButton,
  SafAnchor,
  SafAlert,
  SafBreadcrumb,
  SafBreadcrumbItem,
} from "@saffron/core-components/react";
import { CosmosClient } from "@azure/cosmos";
import { Link } from "react-router-dom";
import "../../../App.css";
import { Breadcrumb } from "../../../shared";

const SELECT_ALL = {
  NONE_SELECTED: "NONE_SELECTED",
  ALL_SELECTED: "ALL_SELECTED",
  SOME_SELECTED: "SOME_SELECTED",
};

const Addruntime = () => {
  const [isVisible, setIsVisible] = useState(false);

  const [data, setData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [controlled, setControlled] = useState({
    totalItemCount: 0,
    currentPageIndex: 1,
    itemsArray: [7, 10, 25, 50, 100, 250, 500],
    itemsPerPage: 7,
  });
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState(null);

  const endpoint = process.env.REACT_APP_COSMOS_ENDPOINT;
  const key = process.env.REACT_APP_COSMOS_KEY;
  const client = useMemo(
    () => new CosmosClient({ endpoint, key }),
    [endpoint, key]
  );

  const databaseId = process.env.REACT_APP_DB_SOURCE;
  const containerId = process.env.REACT_APP_CONTAINER_TECH_STACK_MAPPING;

  const navigate = useNavigate();
  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const handleAddRuntime = () => {
    const selectedRows = data.filter((item) => item.isSelected);

    if (selectedRows.length === 0) {
      setAlertMessage("Please select at least one row.");
      setAlertType("warning");
      return;
    }

    const runtimeData = selectedRows.map((item) => ({
      capability: item.capability,
      capabilityItem: item.capabilityItem,
      supportingPattern: "",
      recommendation: item.recommendation,
      provider: item.provider,
    }));

    const assetInfo = JSON.parse(localStorage.getItem("assetInfo"));

    setAlertMessage("Runtime added successfully.");
    setAlertType("success");
    setTimeout(() => {
      setAlertMessage("");
      navigate("/technology-stacks", { state: { runtimeData, assetInfo } });
    }, 2000);
  };

  const fetchData = useCallback(async () => {
    try {
      const { resources } = await client
        .database(databaseId)
        .container(containerId)
        .items.query("SELECT * FROM c")
        .fetchAll();

      // const transformedData = resources.flatMap((item, itemIndex) =>
      //   item.capabilities.flatMap((capability, capabilityIndex) =>
      //     capability.services.map((service, serviceIndex) => ({
      //       id: `tech-stack-mapping-${itemIndex}-${capabilityIndex}-${serviceIndex}`,
      //       isSelected: false,
      //       capabilityItem: capability.technology_name || "",
      //       capability: service.capability || "",
      //       supportingPattern: "",
      //       recommendation: service.recommendation || "",
      //       provider: service.provider || "",
      //     }))
      //   )
      // );
      const transformedData = resources.flatMap((item, itemIndex) =>
        item.capabilities.flatMap((capability, capabilityIndex) =>
          capability.services.map((service, serviceIndex) => ({
            id: `tech-stack-mapping-${itemIndex}-${capabilityIndex}-${serviceIndex}`,
            isSelected: false,
            capabilityItem: service.technology_name || "",
            capability: capability.capability || "",
            supportingPattern: "",
            recommendation: service.recommendation || "",
            provider: service.provider || "",
          }))
        )
      );

      console.log("Fetched and transformed data:", transformedData);
      setData(transformedData);
    } catch (error) {
      console.error("Error fetching data from Cosmos DB", error);
    }
  }, [client, containerId, databaseId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onClear = useCallback(() => {
    setSearchValue("");
  }, []);

  const filteredData = useMemo(() => {
    if (!searchValue) return data;
    return data.filter((item) =>
      Object.values(item).some((value) =>
        value.toString().toLowerCase().includes(searchValue.toLowerCase())
      )
    );
  }, [data, searchValue]);

  useEffect(() => {
    const filteredCount = filteredData.length;
    setControlled((prev) => ({
      ...prev,
      totalItemCount: filteredCount,
      currentPageIndex: 1,
    }));
  }, [filteredData]);

  const selectAllState = useMemo(() => {
    const length = selectedIds.length;
    if (length === 0) return SELECT_ALL.NONE_SELECTED;
    if (length === data.length) return SELECT_ALL.ALL_SELECTED;
    return SELECT_ALL.SOME_SELECTED;
  }, [selectedIds, data.length]);

  const selectAll = () => {
    if (selectAllState === SELECT_ALL.ALL_SELECTED) {
      setData(data.map((item) => ({ ...item, isSelected: false })));
      setSelectedIds([]);
    } else {
      setData(data.map((item) => ({ ...item, isSelected: true })));
      setSelectedIds(data.map((item) => item.id));
    }
  };

  const handleRowSelect = (id) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.id === id ? { ...item, isSelected: !item.isSelected } : item
      )
    );
    setSelectedIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(id)) {
        return prevSelectedIds.filter((selectedId) => selectedId !== id);
      } else {
        return [...prevSelectedIds, id];
      }
    });
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex =
      (controlled.currentPageIndex - 1) * controlled.itemsPerPage;
    const endIndex = startIndex + controlled.itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, controlled.currentPageIndex, controlled.itemsPerPage]);

  const onPrevious = () => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: Math.max(prev.currentPageIndex - 1, 1),
    }));
  };

  const onNext = () => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: Math.min(
        prev.currentPageIndex + 1,
        Math.ceil(prev.totalItemCount / prev.itemsPerPage)
      ),
    }));
  };

  const onChange = (event) => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: event.detail,
    }));
  };

  const onItemsPerPageChange = (event) => {
    setControlled((prev) => ({
      ...prev,
      itemsPerPage: event.detail,
      currentPageIndex: 1,
    }));
  };

  return (
    <div style={{ marginTop: "48px" }}>
      <div className="breadcrumb-section">
        <SafBreadcrumb>
          <SafBreadcrumbItem>
            <Breadcrumb />
          </SafBreadcrumbItem>
        </SafBreadcrumb>
      </div>

      <br />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <SafText appearance="heading-2xl">
          Add runtime from the database
        </SafText>

        <SafAnchor href="/customruntime">
          <SafText appearance="body-strong-sm" style={{ marginRight: "70px" }}>
            Add custom runtime
          </SafText>
        </SafAnchor>
      </div>

      <p>
        <SafText appearance="body-default-sm">
          Select capability from the database or add your own custom capability
        </SafText>
      </p>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <SafCheckbox
          checked={selectAllState === SELECT_ALL.ALL_SELECTED}
          indeterminate={selectAllState === SELECT_ALL.SOME_SELECTED}
          onChange={selectAll}
          style={{ marginLeft: "16px" }}
        >
          Select all
        </SafCheckbox>
        <div>
          <span
            style={{
              font: "var(--saf-type-body-default-md-strong-standard)",
              marginRight: "10px",
            }}
          >
            Search
          </span>
          <SafSearchField
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            onClear={onClear}
            style={{ width: "200px", marginRight: "70px" }}
          />
        </div>
      </div>
      <SafTable
        alternating-rows="true"
        density="inherit"
        header-background="default"
        inline-borders="true"
        style={{ width: "100%" }}
      >
        <table className="mytable" style={{ width: "95%" }}>
          <thead>
            <tr>
              <th style={{ width: "50px" }}>Select</th>

              <th
                onClick={() => toggleVisibility()}
                style={{ width: "200px", position: "relative" }}
              >
                Capability
                <SafIcon iconName={isVisible ? "caret-down" : "caret-right"} />
                {isVisible && (
                  <div
                    style={{
                      listStyleType: "none",
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      marginTop: "2px",
                      padding: "5px",
                      border: "1px solid #ccc",
                      backgroundColor: "#fff",
                      zIndex: 1,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      width: "158px",
                    }}
                  >
                    <SafText>Filter</SafText>
                    <br />
                    <SafText>Sort</SafText>
                  </div>
                )}
              </th>

              <th
                onClick={() => handleSort("capabilityItem")}
                style={{ width: "200px" }}
              >
                Technology Name
                <SafIcon
                  iconName={
                    sortConfig.key === "capabilityItem"
                      ? sortConfig.direction === "ascending"
                        ? "arrow-up"
                        : "arrow-down"
                      : "arrow-up-arrow-down"
                  }
                />
              </th>
              <th style={{ width: "200px" }}>Supporting Pattern</th>
              <th
                onClick={() => handleSort("recommendation")}
                style={{ width: "200px" }}
              >
                Recommendation
                <SafIcon
                  iconName={
                    sortConfig.key === "recommendation"
                      ? sortConfig.direction === "ascending"
                        ? "arrow-up"
                        : "arrow-down"
                      : "arrow-up-arrow-down"
                  }
                />
              </th>
              <th
                onClick={() => handleSort("provider")}
                style={{ width: "200px" }}
              >
                Provider
                <SafIcon
                  iconName={
                    sortConfig.key === "provider"
                      ? sortConfig.direction === "ascending"
                        ? "arrow-up"
                        : "arrow-down"
                      : "arrow-up-arrow-down"
                  }
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, index) => (
              <tr key={index}>
                <td>
                  <SafCheckbox
                    checked={item.isSelected}
                    onChange={() => handleRowSelect(item.id)}
                  />
                </td>
                <td>{item.capability || ""}</td>
                <td>{item.capabilityItem || ""}</td>
                <td>{item.supportingPattern || ""}</td>
                <td>{item.recommendation || ""}</td>
                <td>{item.provider || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SafTable>
      <div className="Component-demo">
        <SafPagination
          data-testid="controlled"
          controlled={true}
          totalItemCount={controlled.totalItemCount}
          currentPageIndex={controlled.currentPageIndex}
          itemsArray={controlled.itemsArray}
          itemsPerPage={controlled.itemsPerPage}
          onPrevious={onPrevious}
          onNext={onNext}
          onChange={onChange}
          onItemsPerPageChange={onItemsPerPageChange}
          style={{ width: "95%" }}
        />
      </div>
      <hr style={{ margin: "50px 0 20px 0" }}></hr>
      <div style={{ width: "70%" }}>
        <div style={{ justifyContent: "space-between" }}>
          <Link to="/technology-stacks">
            <SafButton appearance="secondary">Cancel</SafButton>
          </Link>
          <SafButton
            appearance="primary"
            style={{ marginLeft: "20px" }}
            onClick={handleAddRuntime}
          >
            Add to Runtime
          </SafButton>
        </div>
      </div>

      {alertMessage && (
        <SafAlert
          type="toast"
          appearance={alertType}
          onClose={() => setAlertMessage("")}
        >
          {alertMessage}
        </SafAlert>
      )}
    </div>
  );
};

export default Addruntime;
